/**
 * Scan History Page - View and manage historical scans
 *
 * Displays a comprehensive list of user's scan history with
 * filtering, sorting, and detailed views.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNowMs } from '@/hooks/useNowMs';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Eye,
  Trash2,
  ScanLine,
  Loader2,
  Calendar,
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { toast } from '@/hooks/useToast';
import { AppHeader } from '@/components/AppHeader';
import { PageBackground } from '@/components/ui/PageLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PageSubHeader } from '@/components/ui/PageSubHeader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  useReports,
  useDeleteReport,
  useReportStats,
  REPORTS_QUERY_KEYS,
} from '@/hooks/useReports';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import RiskBadge from '@/components/report/RiskBadge';
import { ScanModal } from '@/components/dashboard';
import { useActiveScans } from '@/hooks/useActiveScans';
import { reconcileActiveScans } from '@/lib/scan-reconcile';
import { getEffectiveRiskLevelWithFindings } from '@/lib/theme-config';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

type SortField = 'scannedAt' | 'target' | 'riskScore';
type SortDirection = 'asc' | 'desc';

/** Scans older than this are considered zombie/stale and excluded from active tracking. */
const ACTIVE_SCAN_CUTOFF_MS = 2 * 60 * 60 * 1000;

/** Strip common URL prefixes to show just owner/repo (or owner/repo @ branch). */
function formatTarget(target: string, branch?: string): string {
  const short = target
    .replace(/^https?:\/\/github\.com\//i, '')
    .replace(/^https?:\/\//i, '')
    .replace(/\.git$/, '');
  return branch ? `${short} @ ${branch}` : short;
}

export default function ScanHistoryPage() {
  useDocumentTitle('Scan History');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('scannedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedScans, setSelectedScans] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const itemsPerPage = 20;

  const handleScanModalChange = useCallback(
    (open: boolean) => {
      setScanModalOpen(open);
      if (!open) {
        queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEYS.all });
      }
    },
    [queryClient]
  );

  const { data: reportsData, isLoading, error, refetch } = useReports(100, 0);
  const { data: statsData } = useReportStats();
  const deleteReportMutation = useDeleteReport();
  const { activeScans, addScan, removeScan } = useActiveScans();

  const handleDeleteReport = async (reportId: string) => {
    try {
      await deleteReportMutation.mutateAsync(reportId);
      toast({ title: 'Report deleted', description: 'The scan report has been removed.' });
    } catch (error) {
      console.error(
        'Failed to delete report:',
        error instanceof Error ? error.message : 'Unknown error'
      );
      toast({ title: 'Delete failed', description: 'Could not delete the report. Please try again.', variant: 'destructive' });
    }
    setDeleteTarget(null);
  };

  const reports = reportsData?.reports || [];
  const nowMs = useNowMs();

  // Deduplicate: exclude reports already shown in the activeScans spinner list
  const activeScanIds = useMemo(() => new Set(activeScans.map((s) => s.scanId)), [activeScans]);

  // Reconcile the active-scan queue against the authoritative reports list:
  //   - add recent in-progress reports (e.g. MCP-initiated scans) so they get
  //     polled, show spinners, and fire a toast on completion;
  //   - remove any scan the list now reports as terminal, so the spinner clears
  //     on its own. Without the removal half, a completed scan stayed in the
  //     queue (the getScan poll evicts it but this effect re-added it while the
  //     Prisma row still read "running"), leaving the row stuck at "Scanning…"
  //     until a manual refresh. useReports now polls while anything is pending,
  //     so the list converges to the terminal status and this removal fires.
  // Scans older than 2 hours are considered zombie/stale and excluded — they
  // will never complete and should not be treated as actively running.
  useEffect(() => {
    const { toAdd, toRemove } = reconcileActiveScans(
      reports,
      activeScanIds,
      nowMs,
      ACTIVE_SCAN_CUTOFF_MS
    );
    toRemove.forEach(removeScan);
    toAdd.forEach(addScan);
  }, [reports, activeScanIds, addScan, removeScan, nowMs]);

  // Filter and sort reports
  const filteredReports = useMemo(() => {
    const filtered = reports.filter((report) => {
      // Skip reports that are already rendered as active/in-progress scans
      if (activeScanIds.has(report.scanId)) return false;

      const matchesSearch =
        report.target.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.scanId.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || report.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    // Sort
    filtered.sort((a, b) => {
      const multiplier = sortDirection === 'asc' ? 1 : -1;

      if (sortField === 'scannedAt') {
        return multiplier * (new Date(a.scannedAt).getTime() - new Date(b.scannedAt).getTime());
      }

      if (sortField === 'target') {
        return multiplier * a.target.localeCompare(b.target);
      }

      if (sortField === 'riskScore') {
        const scoreA = a.scores?.overall || 0;
        const scoreB = b.scores?.overall || 0;
        return multiplier * (scoreA - scoreB);
      }

      return 0;
    });

    return filtered;
  }, [reports, searchTerm, statusFilter, sortField, sortDirection, activeScanIds]);

  // Pagination
  const paginatedReports = filteredReports.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);

  const getRiskTrend = (score: number) => {
    if (score >= 7) return <TrendingDown className="w-4 h-4 text-red-500" />;
    if (score >= 4) return <Minus className="w-4 h-4 text-yellow-500" />;
    return <TrendingUp className="w-4 h-4 text-emerald-500" />;
  };

  if (error) {
    // Billing/entitlement errors mean no history available yet — show empty state.
    const isEntitlementError =
      error instanceof Error &&
      (error.message.includes('billing') ||
        error.message.includes('entitlement') ||
        error.message.includes('403') ||
        error.message.includes('404') ||
        error.message.includes('no reports') ||
        error.message.includes('subscription'));
    if (isEntitlementError) {
      return (
        <PageBackground>
          <AppHeader />
          <PageSubHeader
            title="Reports"
            subtitle="View and manage your scan reports"
            action={
              <Button
                onClick={() => setScanModalOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white gap-2"
              >
                <ScanLine className="w-4 h-4" />
                Run a Scan
              </Button>
            }
          />
          <ScanModal open={scanModalOpen} onOpenChange={handleScanModalChange} />
          <div className="container mx-auto px-4 pb-8 animate-fade-in pt-12 animate-fade-in">
            <div className="text-center py-16">
              <Target className="w-12 h-12 mx-auto text-zinc-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-white">No scan history yet</h3>
              <p className="text-zinc-400 mb-6">Start by running your first security scan.</p>
              <Button
                onClick={() => setScanModalOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white gap-2"
              >
                <ScanLine className="w-4 h-4" />
                Run a Scan
              </Button>
            </div>
          </div>
        </PageBackground>
      );
    }
    return (
      <PageBackground className="p-8">
        <Card className="max-w-2xl mx-auto bg-zinc-800 border-zinc-700">
          <CardHeader>
            <CardTitle className="text-red-400">Error Loading Scan History</CardTitle>
            <CardDescription className="text-zinc-400">
              Failed to load your scan history. Please try again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => refetch()}
              className="bg-zinc-700 border-zinc-600 text-white hover:bg-zinc-600"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <AppHeader />
      <PageSubHeader
        title="Reports"
        subtitle="View and manage your scan reports"
        action={
          <Button
            onClick={() => setScanModalOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white gap-2"
          >
            <ScanLine className="w-4 h-4" />
            Run a Scan
          </Button>
        }
      />

      <ScanModal open={scanModalOpen} onOpenChange={handleScanModalChange} />

      {/* Stats Cards */}
      {statsData && (
        <div className="container mx-auto px-4 py-6 animate-fade-in">
          <div className="grid gap-4 md:grid-cols-4">
            <Card className="bg-zinc-800 border-zinc-700 transition-all duration-300 hover:border-zinc-600">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-400">Total Scans</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{statsData.totalScans}</div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-800 border-zinc-700 transition-all duration-300 hover:border-zinc-600">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-400">Completed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-emerald-400">
                  {statsData.completedScans}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-800 border-zinc-700 transition-all duration-300 hover:border-zinc-600">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-400">Avg Risk Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  {(statsData.averageRiskScore ?? 0).toFixed(1)}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-800 border-zinc-700 transition-all duration-300 hover:border-zinc-600">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-400">Last Scan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-medium text-zinc-300">
                  {statsData.lastScanDate
                    ? formatDistanceToNow(new Date(statsData.lastScanDate), { addSuffix: true })
                    : 'Never'}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="container mx-auto px-4 py-4 animate-fade-in">
        <Card className="bg-zinc-800 border-zinc-700 transition-all duration-300 hover:border-zinc-600">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    placeholder="Search by target or scan ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-zinc-700 border-zinc-600 text-white placeholder-slate-400"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px] bg-zinc-700 border-zinc-600 text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-700 border-zinc-600">
                  <SelectItem value="all" className="text-white hover:bg-zinc-600">
                    All Status
                  </SelectItem>
                  <SelectItem value="completed" className="text-white hover:bg-zinc-600">
                    Completed
                  </SelectItem>
                  <SelectItem value="failed" className="text-white hover:bg-zinc-600">
                    Failed
                  </SelectItem>
                  <SelectItem value="partial" className="text-white hover:bg-zinc-600">
                    Partial
                  </SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortField} onValueChange={(v) => setSortField(v as SortField)}>
                <SelectTrigger className="w-[180px] bg-zinc-700 border-zinc-600 text-white">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-700 border-zinc-600">
                  <SelectItem value="scannedAt" className="text-white hover:bg-zinc-600">
                    Date
                  </SelectItem>
                  <SelectItem value="target" className="text-white hover:bg-zinc-600">
                    Target
                  </SelectItem>
                  <SelectItem value="riskScore" className="text-white hover:bg-zinc-600">
                    Risk Score
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                className="bg-zinc-700 border-zinc-600 text-white hover:bg-zinc-600"
              >
                {sortDirection === 'asc' ? '↑' : '↓'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setCompareMode(!compareMode);
                  setSelectedScans(new Set());
                }}
                className={compareMode
                  ? 'bg-purple-700 border-purple-500 text-white hover:bg-purple-600'
                  : 'bg-zinc-700 border-zinc-600 text-white hover:bg-zinc-600'
                }
              >
                Compare
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scan Table */}
      <div className="container mx-auto px-4 pb-8 animate-fade-in">
        <Card className="bg-zinc-800 border-zinc-700 transition-all duration-300 hover:border-zinc-600">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
              </div>
            ) : filteredReports.length === 0 && activeScans.length === 0 ? (
              <div className="text-center py-12">
                <Target className="w-12 h-12 mx-auto text-zinc-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-white">No scans found</h3>
                <p className="text-zinc-400 mb-4">
                  {searchTerm || statusFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Start by running your first security scan'}
                </p>
                <Button
                  onClick={() => setScanModalOpen(true)}
                  className="bg-green-600 hover:bg-green-700 text-white gap-2"
                >
                  <ScanLine className="w-4 h-4" />
                  Run a Scan
                </Button>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-700 hover:bg-zinc-800">
                      {compareMode && <TableHead className="text-zinc-400 w-10"></TableHead>}
                      <TableHead className="text-zinc-400">Target</TableHead>
                      <TableHead className="text-zinc-400">Type</TableHead>
                      <TableHead className="text-zinc-400">Risk Score</TableHead>
                      <TableHead className="text-zinc-400">Status</TableHead>
                      <TableHead className="text-zinc-400">Scanned</TableHead>
                      <TableHead className="text-right text-zinc-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Active (in-progress) scans */}
                    {activeScans.map((scan) => (
                      <TableRow
                        key={`active-${scan.scanId}`}
                        className="border-zinc-700 bg-zinc-800/50"
                      >
                        <TableCell className="font-medium text-zinc-400">
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
                            <span className="truncate max-w-[300px]">{formatTarget(scan.target, scan.branch)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="bg-zinc-700/50 border-zinc-600 text-zinc-400"
                          >
                            {scan.targetType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="h-1.5 w-16 rounded-full bg-zinc-700 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-emerald-500/60"
                              style={{
                                width: scan.status === 'running' ? '60%' : '20%',
                                transition: 'width 1s ease-in-out',
                                animation: 'pulse 2s ease-in-out infinite',
                              }}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="bg-emerald-900/30 border-emerald-700 text-emerald-400"
                          >
                            {scan.status === 'running' ? 'Scanning...' : 'Queued'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-zinc-500">
                            <Calendar className="w-4 h-4" />
                            {scan.createdAt && !isNaN(new Date(scan.createdAt).getTime())
                              ? formatDistanceToNow(new Date(scan.createdAt), { addSuffix: true })
                              : '—'}
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-zinc-500 text-sm">—</TableCell>
                      </TableRow>
                    ))}
                    {/* Completed reports */}
                    {paginatedReports.map((report) => {
                      // Recent in-progress reports (e.g. MCP-initiated scans) that haven't
                      // been registered in the active queue yet: render as non-clickable
                      // spinner rows so users can't view partial/invalid scores.
                      // Older stale/zombie scans fall through to the normal row.
                      const isRecentInProgress =
                        (report.status === 'queued' || report.status === 'running') &&
                        new Date(report.scannedAt).getTime() > nowMs - ACTIVE_SCAN_CUTOFF_MS;
                      if (isRecentInProgress) {
                        return (
                          <TableRow
                            key={report.scanId}
                            className="border-zinc-700 bg-zinc-800/50"
                          >
                            <TableCell className="font-medium text-zinc-400">
                              <div className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
                                <span className="truncate max-w-[300px]">{formatTarget(report.target, report.branch)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className="bg-zinc-700/50 border-zinc-600 text-zinc-400"
                              >
                                {report.targetType}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="h-1.5 w-16 rounded-full bg-zinc-700 overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-emerald-500/60"
                                  style={{
                                    width: report.status === 'running' ? '60%' : '20%',
                                    transition: 'width 1s ease-in-out',
                                    animation: 'pulse 2s ease-in-out infinite',
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className="bg-emerald-900/30 border-emerald-700 text-emerald-400"
                              >
                                {report.status === 'running' ? 'Scanning...' : 'Queued'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2 text-sm text-zinc-500">
                                <Calendar className="w-4 h-4" />
                                {report.scannedAt && !isNaN(new Date(report.scannedAt).getTime())
                                  ? formatDistanceToNow(new Date(report.scannedAt), { addSuffix: true })
                                  : '—'}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-zinc-500 text-sm">—</TableCell>
                          </TableRow>
                        );
                      }

                      const isFailed = report.status === 'failed';
                      const overallScore = report.scores?.overall || 0;
                      return (
                        <TableRow
                          key={report.scanId}
                          className="cursor-pointer border-zinc-700 hover:bg-zinc-700 text-white"
                          onClick={() => {
                            if (compareMode) {
                              setSelectedScans((prev) => {
                                const next = new Set(prev);
                                if (next.has(report.scanId)) {
                                  next.delete(report.scanId);
                                } else if (next.size < 2) {
                                  next.add(report.scanId);
                                }
                                return next;
                              });
                            } else {
                              navigate(`/report?scanId=${report.scanId}`);
                            }
                          }}
                        >
                          {compareMode && (
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedScans.has(report.scanId)}
                                onChange={() => {
                                  setSelectedScans((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(report.scanId)) {
                                      next.delete(report.scanId);
                                    } else if (next.size < 2) {
                                      next.add(report.scanId);
                                    }
                                    return next;
                                  });
                                }}
                                className="rounded border-zinc-600 bg-zinc-900 text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-medium text-white">
                            <div className="flex items-center gap-2">
                              {isFailed ? (
                                <XCircle className="w-4 h-4 text-zinc-400" />
                              ) : (
                                getRiskTrend(overallScore)
                              )}
                              <span className="truncate max-w-[300px]" title={report.target}>{formatTarget(report.target, report.branch)}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className="bg-zinc-700 border-zinc-600 text-white"
                            >
                              {' '}
                              {report.targetType}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <RiskBadge
                              level={
                                isFailed
                                  ? 'failed'
                                  : getEffectiveRiskLevelWithFindings(report.recommendation, report.scores?.overall, report.findingSummary)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={report.status} />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm text-zinc-400">
                              <Calendar className="w-4 h-4" />
                              {report.scannedAt && !isNaN(new Date(report.scannedAt).getTime())
                                ? formatDistanceToNow(new Date(report.scannedAt), {
                                    addSuffix: true,
                                  })
                                : '—'}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/report?scanId=${report.scanId}`);
                              }}
                              className="text-zinc-400 hover:text-white hover:bg-zinc-700"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(report.scanId);
                              }}
                              disabled={deleteReportMutation.isPending}
                              className="text-zinc-400 hover:text-red-400 hover:bg-zinc-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Compare button (visible when 2 scans selected) */}
                {compareMode && selectedScans.size === 2 && (
                  <div className="flex justify-center py-4 border-t border-zinc-700">
                    <Button
                      onClick={() => {
                        const [s1, s2] = [...selectedScans];
                        navigate(`/compare?scan1=${s1}&scan2=${s2}`);
                      }}
                      className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                    >
                      Compare Selected Scans
                    </Button>
                  </div>
                )}
                {compareMode && selectedScans.size < 2 && selectedScans.size > 0 && (
                  <div className="text-center py-3 border-t border-zinc-700">
                    <p className="text-sm text-zinc-400">Select one more scan to compare</p>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-700">
                    <div className="text-sm text-zinc-400">
                      Showing {currentPage * itemsPerPage + 1} to{' '}
                      {Math.min((currentPage + 1) * itemsPerPage, filteredReports.length)} of{' '}
                      {filteredReports.length} results
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 0}
                        onClick={() => setCurrentPage(currentPage - 1)}
                        className="bg-zinc-700 border-zinc-600 text-white hover:bg-zinc-600 disabled:bg-zinc-800 disabled:text-zinc-500"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }).map((_, i) => (
                          <Button
                            key={i}
                            variant={currentPage === i ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentPage(i)}
                            className={`w-8 ${currentPage === i ? 'bg-zinc-600 text-white' : 'bg-zinc-700 border-zinc-600 text-white hover:bg-zinc-600'}`}
                          >
                            {i + 1}
                          </Button>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage >= totalPages - 1}
                        onClick={() => setCurrentPage(currentPage + 1)}
                        className="bg-zinc-700 border-zinc-600 text-white hover:bg-zinc-600 disabled:bg-zinc-800 disabled:text-zinc-500"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete scan report?"
        description="This will permanently remove the report and its results. This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={() => { if (deleteTarget) handleDeleteReport(deleteTarget); }}
      />
    </PageBackground>
  );
}
